import * as signalR from "@microsoft/signalr";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  HosDriverProfileDto,
  Vehicle,
  VehicleLocation,
  vehiclesApi,
} from "../api/vehiclesApi";
import { anyTrackApi } from "../api/anyTrackApi";
import { useAppDispatch, useAppSelector } from "../app/hooks";

interface SignalRContextType {
  // Connection status
  isConnected: boolean;
  isConnecting: boolean;
  connection: signalR.HubConnection | null;

  // Connection methods
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;

  // Data refresh methods
  refreshVehiclesData: () => Promise<void>;
  refreshHosData: () => Promise<void>;

  // Vehicle history methods
  getVehicleLocationHistory: (
    vehicleId: number,
    limit?: number,
    daysBack?: number
  ) => Promise<void>;

  // Focused vehicle methods
  subscribeVehicleFocus: (vehicleId: number) => Promise<void>;
  unsubscribeVehicleFocus: () => Promise<void>;

  // Callback setters
  setVehicleHistoryCallback: (
    callback?: (history: VehicleLocation[]) => void
  ) => void;
  setVehicleUpdateCallback: (
    callback?: (update: VehicleLocation) => void
  ) => void;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

interface SignalRProviderProps {
  children: React.ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  );

  const dispatch = useAppDispatch();

  // Get token from Redux store
  const token = useAppSelector((state) => state.auth.token);

  // Ref to prevent multiple connection attempts
  const connectionAttemptedRef = useRef(false);

  // Callback refs for decoupled communication
  const onVehicleHistoryReceivedRef = useRef<
    ((history: VehicleLocation[]) => void) | undefined
  >(undefined);
  const onVehicleUpdateReceivedRef = useRef<
    ((update: VehicleLocation) => void) | undefined
  >(undefined);

  // const dispatch = useDispatch();

  // Function to set vehicle history callback
  const setVehicleHistoryCallback = useCallback(
    (callback?: (history: VehicleLocation[]) => void) => {
      onVehicleHistoryReceivedRef.current = callback;
    },
    []
  );

  // Function to set vehicle update callback
  const setVehicleUpdateCallback = useCallback(
    (callback?: (update: VehicleLocation) => void) => {
      onVehicleUpdateReceivedRef.current = callback;
    },
    []
  );

  // Get JWT token from Redux store
  const getJwtToken = useCallback(() => {
    return token;
  }, [token]);

  // Function to update specific vehicle fields - simplified approach
  const updateVehicleFields = useCallback(
    (vehicleId: number, updates: Partial<Vehicle>) => {
      try {
        dispatch(
          vehiclesApi.util.updateQueryData(
            "getVehicles",
            undefined,
            (draft) => {
              if (!draft) {
                return;
              }

              const vehicle = draft.find((v) => v.id === vehicleId);
              if (vehicle) {
                Object.assign(vehicle, updates);
              }
            }
          )
        );
      } catch (error) {
        console.warn("⚠️ Unable to update vehicle cache", error);
      }
    },
    [dispatch]
  );

  // Ref to track the currently focused vehicle for reconnect handling
  const focusedVehicleIdRef = useRef<number | null>(null);

  // Shared helper: apply a single location update to RTK cache + callback
  const applyLocationUpdate = useCallback(
    (update: VehicleLocation) => {
      updateVehicleFields(update.vehicleId, {
        lastLatitude: update.latitude,
        lastLongitude: update.longitude,
        lastSpeed: update.speed,
        lastStatus: update.status,
        lastLocationUpdate: update.timestamp,
        lastHeading: update.heading ?? null,
        // Always apply effective address from server (including null clear after stale-address guard).
        lastAddress: update.formattedAddress ?? null,
      });

      if (onVehicleUpdateReceivedRef.current) {
        onVehicleUpdateReceivedRef.current(update);
      }
    },
    [updateVehicleFields]
  );

  // Setup SignalR event handlers
  const setupEventHandlers = useCallback(
    (newConnection: signalR.HubConnection) => {
      // Vehicle update events - only update specific fields
      newConnection.on(
        "ReceiveVehicleUpdates",
        (updates: VehicleLocation[]) => {
          console.log("📡 Received vehicle updates:", updates);
          updates.forEach(applyLocationUpdate);
        }
      );

      // Focused vehicle update (10s targeted)
      newConnection.on(
        "ReceiveFocusedVehicleUpdate",
        (update: VehicleLocation) => {
          console.log("📡 Received focused vehicle update:", update);
          applyLocationUpdate(update);
        }
      );

      // All vehicles events - log for now, hooks will handle the data
      newConnection.on("ReceiveAllVehicles", (allVehicles: Vehicle[]) => {
        console.log("📡 Received all vehicles:", allVehicles.length);
        // The hooks will handle this data through their own queries
      });

      // Also listen for the alternative event name
      newConnection.on("ReceiveVehicleLocations", (allVehicles: Vehicle[]) => {
        console.log("📡 Received vehicle locations:", allVehicles.length);
        // The hooks will handle this data through their own queries
      });

      // Vehicle fault updates - update only fault-related fields
      newConnection.on(
        "ReceiveVehicleFaultUpdates",
        (updatedVehicles: Vehicle[]) => {
          console.log("📡 Received vehicle fault updates:", updatedVehicles);

          updatedVehicles.forEach((updatedVehicle) => {
            updateVehicleFields(updatedVehicle.id, {
              lastFaultTime: updatedVehicle.lastFaultTime,
              lastFaultCanBusType: updatedVehicle.lastFaultCanBusType,
              lastFaultSpnDescription: updatedVehicle.lastFaultSpnDescription,
              lastFaultFmiDescription: updatedVehicle.lastFaultFmiDescription,
              lastDtcDescription: updatedVehicle.lastDtcDescription,
              lastDtcCode: updatedVehicle.lastDtcCode,
              lastEmissionsLightOn: updatedVehicle.lastEmissionsLightOn,
              lastProtectLightOn: updatedVehicle.lastProtectLightOn,
              lastStopLightOn: updatedVehicle.lastStopLightOn,
              lastWarningLightOn: updatedVehicle.lastWarningLightOn,
              lastCheckEngineLightOn: updatedVehicle.lastCheckEngineLightOn,
            });
          });
        }
      );

      // Vehicle history events
      newConnection.on(
        "ReceiveVehicleLocationHistory",
        (history: VehicleLocation[]) => {
          console.log("📡 Received vehicle location history:", history.length);
          if (onVehicleHistoryReceivedRef.current) {
            onVehicleHistoryReceivedRef.current(history);
          }
        }
      );

      // Also listen for alternative event name
      newConnection.on(
        "ReceiveVehicleHistory",
        (history: VehicleLocation[]) => {
          console.log("📡 Received vehicle history:", history.length);
          if (onVehicleHistoryReceivedRef.current) {
            onVehicleHistoryReceivedRef.current(history);
          }
        }
      );

      // HOS update events - log for now, hooks will handle the data
      newConnection.on(
        "ReceiveHosUpdates",
        (profiles: HosDriverProfileDto[]) => {
          console.log("📡 Received HOS updates:", profiles.length);
          // The hooks will handle this data through their own queries
        }
      );

      // Real-time vehicle update events
      newConnection.on("ReceiveVehicleUpdate", (update: VehicleLocation) => {
        console.log("📡 Received single vehicle update:", update);
        if (onVehicleUpdateReceivedRef.current) {
          onVehicleUpdateReceivedRef.current(update);
        }
      });

      // Trailer unplug alerts - invalidate AnyTrack cache to trigger re-fetch
      newConnection.on("ReceiveTrailerUnplugAlerts", (alerts: unknown[]) => {
        console.warn("Trailer unplug alert received:", alerts);
        dispatch(anyTrackApi.util.invalidateTags(["AnyTrackLocations"]));
      });

      // Gateway unplug alerts - invalidate vehicles cache to trigger re-fetch
      newConnection.on("ReceiveGatewayUnplugAlerts", (alerts: unknown[]) => {
        console.warn("Gateway unplug alert received:", alerts);
        dispatch(vehiclesApi.util.invalidateTags(["Vehicles"]));
      });

      // Connection status events
      newConnection.onclose((error) => {
        console.log("🔌 SignalR connection closed", error);
        setIsConnected(false);
        setConnection(null);
      });

      newConnection.onreconnecting((error) => {
        console.log("🔄 SignalR reconnecting", error);
        setIsConnecting(true);
      });

      newConnection.onreconnected((connectionId) => {
        console.log("✅ SignalR reconnected", connectionId);
        setIsConnected(true);
        setIsConnecting(false);
      });
    },
    [updateVehicleFields, applyLocationUpdate]
  );

  const connect = useCallback(async () => {
    if (isConnecting || isConnected || connectionAttemptedRef.current) return;

    try {
      setIsConnecting(true);
      connectionAttemptedRef.current = true;

      const jwtToken = getJwtToken();
      if (!jwtToken) {
        throw new Error("No JWT token found");
      }

      const baseUrl =
        process.env.REACT_APP_API_URL || "http://localhost:5071/api";
      const hubUrl = `${baseUrl.replace("/api", "")}/hubs/fleet`;
      console.log("🔌 SignalR URL:", hubUrl);

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
          accessTokenFactory: () => jwtToken,
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      setupEventHandlers(newConnection);
      await newConnection.start();

      setConnection(newConnection);
      setIsConnected(true);

      // Request initial data
      try {
        await newConnection.invoke("RefreshVehiclesData");
        await newConnection.invoke("RefreshHosData");
      } catch (error) {
        console.warn("⚠️ Error requesting initial data:", error);
        // Silent fail for initial data requests
      }
    } catch (error) {
      console.error("❌ Error connecting to SignalR:", error);
      setIsConnected(false);
      connectionAttemptedRef.current = false; // Reset on error to allow retry
    } finally {
      setIsConnecting(false);
    }
  }, [getJwtToken, setupEventHandlers, isConnecting, isConnected]);

  const disconnect = useCallback(async () => {
    if (connection) {
      try {
        await connection.stop();
        setConnection(null);
        setIsConnected(false);
        connectionAttemptedRef.current = false; // Reset to allow reconnection
      } catch (error) {
        console.error("❌ Error disconnecting:", error);
      }
    }
  }, [connection]);

  const refreshVehiclesData = useCallback(async () => {
    if (connection?.state === signalR.HubConnectionState.Connected) {
      try {
        await connection.invoke("RefreshVehiclesData");
      } catch (error) {
        console.error("❌ Error refreshing vehicles data:", error);
      }
    }
  }, [connection]);

  const refreshHosData = useCallback(async () => {
    if (connection?.state === signalR.HubConnectionState.Connected) {
      try {
        await connection.invoke("RefreshHosData");
      } catch (error) {
        console.error("❌ Error refreshing HOS data:", error);
      }
    }
  }, [connection]);

  // Get vehicle location history through SignalR
  const getVehicleLocationHistory = useCallback(
    async (vehicleId: number, limit: number = 100, daysBack: number = 1) => {
      if (connection?.state === signalR.HubConnectionState.Connected) {
        try {
          console.log("📍 Requesting vehicle location history:", {
            vehicleId,
            limit,
            daysBack,
          });
          await connection.invoke(
            "GetVehicleLocationHistory",
            vehicleId,
            limit,
            daysBack
          );
        } catch (error) {
          console.error("❌ Error requesting vehicle location history:", error);
          throw error;
        }
      } else {
        throw new Error("SignalR connection not available");
      }
    },
    [connection]
  );

  const subscribeVehicleFocus = useCallback(
    async (vehicleId: number) => {
      if (connection?.state !== signalR.HubConnectionState.Connected) return;
      try {
        focusedVehicleIdRef.current = vehicleId;
        await connection.invoke("SubscribeVehicleFocus", vehicleId);
        console.log("📡 Subscribed to vehicle focus:", vehicleId);
      } catch (error) {
        console.error("❌ Error subscribing to vehicle focus:", error);
      }
    },
    [connection]
  );

  const unsubscribeVehicleFocus = useCallback(async () => {
    if (connection?.state !== signalR.HubConnectionState.Connected) return;
    try {
      focusedVehicleIdRef.current = null;
      await connection.invoke("UnsubscribeVehicleFocus");
      console.log("📡 Unsubscribed from vehicle focus");
    } catch (error) {
      console.error("❌ Error unsubscribing from vehicle focus:", error);
    }
  }, [connection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [connection]);

  // Auto-connect when JWT token is available
  useEffect(() => {
    if (
      token &&
      !isConnected &&
      !isConnecting &&
      !connectionAttemptedRef.current
    ) {
      connect();
    }
  }, [token, isConnected, isConnecting, connect]);

  const value: SignalRContextType = {
    isConnected,
    isConnecting,
    connection,
    connect,
    disconnect,
    refreshVehiclesData,
    refreshHosData,
    getVehicleLocationHistory,
    subscribeVehicleFocus,
    unsubscribeVehicleFocus,
    setVehicleHistoryCallback,
    setVehicleUpdateCallback,
  };

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (context === undefined) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};
